import axios from "axios";
import { useEffect, useState } from "react";
import styles from "./AssignComponent.module.css";

const AssignComponent = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(5); // Number of assignments per page
  const token = localStorage.getItem("token");

  useEffect(() => {
    // Fetch campaigns
    axios
      .get(
        "https://prod.toxboxai.com/api/campaign/getallcampaign",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        // Filter campaigns to only include those with campaignCurrentStatus: "enabled"
        const enabledCampaigns = response.data.data.campaigns.filter(
          (campaign) => campaign.campaignCurrentStatus === "enabled"
        );
        setCampaigns(enabledCampaigns);
      })
      .catch((error) => console.error("Error fetching campaigns:", error));

    // Fetch participants
    axios
      .get(
        "https://prod.toxboxai.com/api/vendor/participants",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => setParticipants(response.data.data))
      .catch((error) => console.error("Error fetching participants:", error));

    // Fetch assignments
    axios
      .get(
        "https://prod.toxboxai.com/api/campaign/assignments",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => setAssignments(response.data.data.campaigns))
      .catch((error) => console.error("Error fetching assignments:", error));
  }, [token]);

  // Filter campaigns that are NOT assigned yet and are enabled
  const availableCampaigns = campaigns.filter((campaign) => {
    const assignedCampaign = assignments.find((c) => c._id === campaign._id);
    return (
      (!assignedCampaign ||
        assignedCampaign.assignedParticipants.length === 0) &&
      campaign.campaignCurrentStatus === "enabled"
    );
  });

  const handleAssign = () => {
    if (selectedCampaign && selectedParticipant) {
      const payload = {
        participantId: selectedParticipant,
        campaignId: selectedCampaign,
      };
      axios
        .post(
          "https://prod.toxboxai.com/api/campaign/assigncampaign",
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then((response) => {
          console.log("Assignment successful:", response.data);
          // Refresh assignments after successful assignment
          axios
            .get(
              "https://prod.toxboxai.com/api/campaign/assignments",
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
            .then((response) => setAssignments(response.data.data.campaigns))
            .catch((error) =>
              console.error("Error refreshing assignments:", error)
            );
          // Clear selections after assignment
          setSelectedCampaign("");
          setSelectedParticipant("");
        })
        .catch((error) => console.error("Error assigning campaign:", error));
    }
  };

  // Calculate total pages based on assignments
  useEffect(() => {
    const totalAssignments = assignments.reduce(
      (count, campaign) => count + campaign.assignedParticipants.length,
      0
    );
    setTotalPages(Math.ceil(totalAssignments / itemsPerPage));
  }, [assignments, itemsPerPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleGoToPage = (e) => {
    const page = parseInt(e.target.previousSibling.value, 10);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Paginate assignments
  const paginatedAssignments = [];
  let currentIndex = 0;
  assignments.forEach((campaign) => {
    campaign.assignedParticipants.forEach((participant) => {
      if (
        currentIndex >= (currentPage - 1) * itemsPerPage &&
        currentIndex < currentPage * itemsPerPage
      ) {
        paginatedAssignments.push({ campaign, participant });
      }
      currentIndex++;
    });
  });

  return (
    <div className={styles.container}>
      <div className={styles.selectWrapper}>
        <select
          value={selectedCampaign}
          onChange={(e) => setSelectedCampaign(e.target.value)}
          className={styles.select}
        >
          <option value="">Select Campaign</option>
          {availableCampaigns.map((campaign) => (
            <option key={campaign._id} value={campaign._id}>
              {campaign.campaignname || "Unnamed Campaign"}
            </option>
          ))}
        </select>
        <select
          value={selectedParticipant}
          onChange={(e) => setSelectedParticipant(e.target.value)}
          className={styles.select}
        >
          <option value="">Select Participant</option>
          {participants.map((participant) => (
            <option key={participant._id} value={participant._id}>
              {participant.name}
            </option>
          ))}
        </select>
        <button onClick={handleAssign} className={styles.button}>
          Assign
        </button>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Participant</th>
            <th>Campaign</th>
            {/* <th>Project</th> */}
            <th>Developer</th>
          </tr>
        </thead>
        <tbody>
          {paginatedAssignments.map(({ campaign, participant }) => (
            <tr key={`${campaign._id}-${participant._id}`}>
              <td>{participant.name}</td>
              <td>{campaign.campaignname}</td>
              {/* <td>{campaign?.project?.projectname || "N/A"}</td> */}
              <td>{campaign.createdBy?.email || "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.pageButton}
        >
          Previous
        </button>
        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`${styles.pageButton} ${currentPage === page ? styles.active : ""
              }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.pageButton}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AssignComponent;
